import { useEffect, useMemo, useRef, useState } from "react";
import { cldLqip } from "@/lib/utils";

/**
 * ProgressiveImage
 *
 * Two-layer render:
 *   • A background <div> that carries the tiny (~1 KB) Cloudinary LQIP
 *     as its own background-image. This layer stays at opacity 1, so
 *     the blurred preview is visible the instant its bytes land — even
 *     while the sharp image is still downloading.
 *   • The sharp <img> sits on top, starts at opacity 0, and fades in
 *     (via the `.gl-img` → `.gl-img--loaded` transition) once it's
 *     decoded.
 *
 * We MUST NOT paint the LQIP on the <img> itself because `.gl-img`
 * keeps that element at opacity: 0 until load — which would hide the
 * blur too. Previously the "blur only appears with the sharp image"
 * bug came from exactly that mistake.
 *
 * The wrapper's className receives whatever positioning classes the
 * caller passed (e.g. `absolute inset-0 w-full h-full object-cover`
 * from the collection grid). Both inner layers fill it 100%. When the
 * wrapper class includes hover behaviour (e.g. `md:group-hover:opacity-0`
 * for the card-swap effect), fading the wrapper takes both layers with
 * it — semantically identical to the pre-refactor single-<img> version.
 *
 * If the source is not Cloudinary (local `/items/*`, admin uploads
 * from other hosts) we skip the LQIP layer entirely and render a bare
 * <img> — those already load fast enough that a blur buys nothing.
 */
const ProgressiveImage = ({
  src,
  alt = "",
  className = "",
  priority = false,
  sizes,
  srcSet,
  style,
  position,
  ...rest
}) => {
  const posStyle = position ? { objectPosition: position } : undefined;
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);

  const lqip = useMemo(() => cldLqip(src), [src]);

  // Cache-hit case: if the browser already had the sharp image (e.g.
  // returning to the collection from a PDP), the load event may have
  // fired before React attached its listener.
  useEffect(() => {
    if (ref.current?.complete && ref.current?.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  // No LQIP → drop back to the plain img; keeps hover-swap targets
  // (like `gl-card-swap-front`) working exactly as before.
  if (!lqip) {
    return (
      <img
        ref={ref}
        src={src}
        alt={alt}
        srcSet={srcSet}
        sizes={sizes}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        onLoad={() => setLoaded(true)}
        style={posStyle ? { ...style, ...posStyle } : style}
        className={`gl-img ${loaded ? "gl-img--loaded" : ""} ${className}`}
        {...rest}
      />
    );
  }

  // The two inner layers rely on the wrapper being a positioning
  // context. Any `absolute` / `fixed` in the caller's className already
  // establishes one; otherwise inject `relative` so the inner
  // absolutely-positioned layers stay contained.
  const isPositioned = /\b(absolute|fixed|relative|sticky)\b/.test(className);
  const wrapperClass = isPositioned ? className : `relative ${className}`;

  return (
    <div className={wrapperClass} style={style} aria-hidden={rest["aria-hidden"]}>
      {/* LQIP layer — stays visible the entire time the sharp image is
          downloading. Rendered BELOW the <img> in DOM order, and both
          layers are absolutely positioned so they overlap perfectly. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 w-full h-full"
        style={{
          backgroundImage: `url("${lqip}")`,
          backgroundSize: "cover",
          backgroundPosition: position || "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <img
        ref={ref}
        src={src}
        alt={alt}
        srcSet={srcSet}
        sizes={sizes}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        onLoad={() => setLoaded(true)}
        style={posStyle}
        className={`gl-img ${loaded ? "gl-img--loaded" : ""} absolute inset-0 w-full h-full object-cover`}
        {...rest}
      />
    </div>
  );
};

export default ProgressiveImage;
